import { Chip, Stack, Typography } from "@mui/material";
import {
  EMPTY_VALUE_PLACEHOLDER,
  SystemDropdownCategory,
  formatInsuranceNumber,
  formatShortDate,
  useSystemDropdownOptions,
} from "@shared";

import { ClientLink } from "@/common/components/client-link/ClientLink";
import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";
import { locales } from "@/common/contexts/localization-date-context/LocalizationDateContextProvider.utils";
import { useTranslation } from "@/translations";

import { InternetRegistrationPartyCardProps } from "./InternetRegistrationPartyCard.types";

export function InternetRegistrationPartyCard({ label, icon, tone, party }: InternetRegistrationPartyCardProps) {
  const { t } = useTranslation();
  const { language } = useLanguageContext();
  const { labelOf: statusLabelOf } = useSystemDropdownOptions(SystemDropdownCategory.ClientStatus);

  const accentColor = tone === "new" ? "secondary.main" : "primary.main";

  const rows: Array<{ label: string; value: string }> = [
    { label: t("internetRegistrations.fields.caseNumber"), value: party.caseNumber },
    {
      label: t("internetRegistrations.fields.birthday"),
      value: party.birthday ? formatShortDate(party.birthday, locales[language]) : EMPTY_VALUE_PLACEHOLDER,
    },
    {
      label: t("internetRegistrations.fields.insuranceNumber"),
      value: party.insuranceNumber ? formatInsuranceNumber(party.insuranceNumber) : EMPTY_VALUE_PLACEHOLDER,
    },
    {
      label: t("internetRegistrations.fields.assignedCounselor"),
      value: party.assignedCounselorUserCode ?? EMPTY_VALUE_PLACEHOLDER,
    },
  ];

  return (
    // A named region, because both cards of a pair normally carry the same person's name: without it
    // the two client links are announced identically and nothing says which side is which.
    <Stack component="section" aria-label={label} spacing={1.5} flex={1} minWidth={0}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
        <Stack direction="row" alignItems="center" gap={0.75} color={accentColor} minWidth={0}>
          {icon}
          <Typography variant="overline" color="inherit" fontWeight={700} noWrap>
            {label}
          </Typography>
        </Stack>
        {party.status && <Chip size="small" label={`${party.status} · ${statusLabelOf(party.status)}`} />}
      </Stack>

      <ClientLink clientId={party.clientId} name={`${party.lastName}, ${party.firstName}`} variant="h3" />

      <Stack component="dl" spacing={0.5} m={0}>
        {rows.map((row) => (
          <Stack key={row.label} direction="row" justifyContent="space-between" gap={2}>
            <Typography component="dt" variant="body2" color="text.secondary">
              {row.label}
            </Typography>
            <Typography component="dd" variant="body2" m={0} textAlign="right">
              {row.value}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
}
